import React, { useRef, useState, useEffect } from "react"
import classnames from "../../util/cls"
import Icon from "../icon"
import FullscreenIcon from "@mui/icons-material/Fullscreen"
import styles from "./styles.module.css"
import { Slider, Typography } from "@mui/material"

const VideoEditor = ({ src }) => {
  const isVideo = /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(src)
  const isAudio = /\.(mp3|wav|ogg|flac|aac)$/i.test(src)
  const isImg = /\.(jpeg|png|jpg)$/i.test(src)

  const mediaRef = useRef(null)
  const containerRef = useRef(null)

  const [isPaused, setIsPaused] = useState(true)
  const [progress, setProgress] = useState(0)
  const [bufferProgress, setBufferProgress] = useState(0)
  const [volume, setVolume] = useState(50)
  const [lastVolume, setLastVolume] = useState(50)
  const [isMuted, setIsMuted] = useState(false)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [duration, setDuration] = useState(0.0)
  const [currentTime, setCurrentTime] = useState(0.0)

  useEffect(() => {
    const media = mediaRef.current
    if (!media) return

    const updateBufferProgress = () => {
      if (media.buffered.length > 0) {
        const bufferedEnd = media.buffered.end(media.buffered.length - 1)
        setBufferProgress((bufferedEnd / media.duration) * 100)
      }
    }

    media.addEventListener("progress", updateBufferProgress)
    return () => {
      media.removeEventListener("progress", updateBufferProgress)
    }
  }, [])

  const formatTime = (time) => {
    const minutes = Math.floor((time % 3600) / 60)
    const seconds = Math.floor(time % 60)
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
  }

  const togglePlayPause = () => {
    if (mediaRef.current) {
      if (mediaRef.current.paused) {
        mediaRef.current.play()
        setIsPaused(false)
      } else {
        mediaRef.current.pause()
        setIsPaused(true)
      }
    }
  }

  const handleTimeUpdate = () => {
    if (mediaRef.current) {
      const newProgress = (mediaRef.current.currentTime / mediaRef.current.duration) * 100
      setProgress(newProgress)
      setCurrentTime(mediaRef.current.currentTime)
    }
  }


  const handleSeek = (_, value) => {
    if (mediaRef.current) {
      mediaRef.current.currentTime = value
      setCurrentTime(value)
      setProgress((value / duration) * 100)
    }
  }

  const handleVolumeChange = (_, value) => {
    if (value === 0) {
      setIsMuted(true)
    } else {
      setIsMuted(false)
      setLastVolume(value)
    }

    setVolume(value)
    if (mediaRef.current) {
      mediaRef.current.volume = value / 100
      mediaRef.current.muted = value === 0
    }
  }

  const toggleMute = () => {
    if (isMuted) {
      setVolume(lastVolume)
      if (mediaRef.current) {
        mediaRef.current.volume = lastVolume / 100
        mediaRef.current.muted = false
      }
    } else {
      setLastVolume(volume)
      setVolume(0)
      if (mediaRef.current) {
        mediaRef.current.volume = 0
        mediaRef.current.muted = true
      }
    }
    setIsMuted(!isMuted)
  }

  const handleMetadataLoaded = (e) => {
    setDuration(e.target.duration)
    setCurrentTime(e.target.currentTime)
  }

  const toggleFullScreen = (e) => {
    e.stopPropagation()
    if (!isFullScreen) {
      containerRef.current.requestFullscreen?.()
    } else {
      document.exitFullscreen?.()
    }
    setIsFullScreen(!isFullScreen)
  }

  return (
    <div ref={containerRef} className={styles.videoContainer}>
      {
        ({
          [true]: (
            <div className={styles.audioVisualizer}>
              <audio
                ref={mediaRef}
                src={src}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleMetadataLoaded}
              />
            </div>
          ),
          [isVideo]: (
            <video
              ref={mediaRef}
              src={src}
              className={styles.videoPlayer}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleMetadataLoaded}
              onClick={togglePlayPause}
            />
          ),
          [isImg]: (
            <img
              src={src}
            />
          ),
        })[true]
      }

      {!isImg && (

        <div className={styles.videoFocus}>
          <div className={styles.focusTop}></div>
          <div className={styles.focusBottom}></div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            <div className={styles.bottomControls}>
              <div className={styles.seekBar}>
                <div className={styles.seekSliderContainer}>
                  <Slider
                    className={styles.seekSlider}
                    aria-label="Seek"
                    value={currentTime}
                    max={duration}
                    onChange={handleSeek}
                    onChangeCommitted={(e) => e.stopPropagation()}
                    sx={{
                      "& .MuiSlider-rail": {
                        background: "rgba(255, 255, 255, 0.3)",
                      },
                      "& .MuiSlider-track": {
                        background: "white",
                      },
                      "& .MuiSlider-thumb": {
                        display: "none",
                      },
                      "&:hover .MuiSlider-thumb": {
                        display: "block",
                      },
                      "& .MuiSlider-track::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        height: "100%",
                        width: `${bufferProgress}%`,
                        background: "rgba(255, 255, 255, 0.5)",
                        zIndex: -1,
                      },
                    }}
                  />
                </div>
              </div>

              <div className={styles.videoControls}>
                <div onClick={togglePlayPause} className={classnames(styles.playButton, styles.controlButton)}>
                  {isPaused ? <Icon type="play" /> : <Icon type="pause" />}
                </div>

                <div onClick={toggleMute} className={classnames(styles.volumeButton, styles.controlButton)}>
                  {isMuted ? <Icon type="volume_mute" /> : volume <= 50 ? <Icon type="volume_down" /> : <Icon type="volume_up" />}
                </div>

                <div className={styles.volumeSliderContainer}>
                  <Slider
                    className={styles.volumeSlider}
                    value={volume}
                    onChange={handleVolumeChange}
                    onChangeCommitted={(e) => e.stopPropagation()}
                  />
                </div>

                <div className={styles.timer}>
                  <Typography style={{ color: "white" }}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </Typography>
                </div>

                {isVideo && (
                  <div className={styles.controlButton} style={{ position: "absolute", right: 0 }}>
                    <FullscreenIcon onClick={toggleFullScreen} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VideoEditor
